import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GuardianDetail, GuardianEstado, ListGuardiansResponse } from 'shared';
import { api } from '@/lib/api';

// Encarregados de Educação: staff approve their accounts and link them to the
// participants they look after. A guardian never links themselves.

const BASE = '/api/guardians';

export const guardianKeys = {
  all: ['guardians'] as const,
  list: (estado: GuardianEstado | '') => ['guardians', 'list', estado] as const,
  detail: (id: number) => ['guardians', 'detail', id] as const,
};

/** GET /api/guardians?estado= — newest first. '' lists every state. */
export function useGuardians(estado: GuardianEstado | '') {
  const query = estado ? `?estado=${encodeURIComponent(estado)}` : '';
  return useQuery({
    queryKey: guardianKeys.list(estado),
    queryFn: () => api.get<ListGuardiansResponse>(`${BASE}${query}`),
    staleTime: 15_000,
  });
}

/** GET /api/guardians/{id} — the account, its educandos and the suggestions. */
export function useGuardian(id: number | null) {
  return useQuery({
    queryKey: id === null ? ['guardians', 'detail', 'none'] : guardianKeys.detail(id),
    queryFn: () => api.get<GuardianDetail>(`${BASE}/${id}`),
    enabled: id !== null,
  });
}

/**
 * Every change to a guardian answers with the guardian as it now stands: put
 * that in the cache, and let the lists refetch, since state and counts move.
 */
function useGuardianChange<TVars>(request: (vars: TVars) => Promise<GuardianDetail>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (detail) => {
      queryClient.setQueryData(guardianKeys.detail(detail.guardian.id), detail);
      queryClient.invalidateQueries({ queryKey: ['guardians', 'list'] });
    },
  });
}

/** Approve a pending account, or reopen a closed one. */
export function useActivateGuardian() {
  return useGuardianChange((id: number) => api.post<GuardianDetail>(`${BASE}/${id}/activate`));
}

/** Close an account and sign the guardian out. Their links stay. */
export function useDeactivateGuardian() {
  return useGuardianChange((id: number) => api.post<GuardianDetail>(`${BASE}/${id}/deactivate`));
}

export interface LinkVars {
  guardianId: number;
  participantId: number;
}

/** Link a participant to the guardian. Only staff do this. */
export function useLinkEducando() {
  return useGuardianChange(({ guardianId, participantId }: LinkVars) =>
    api.post<GuardianDetail>(`${BASE}/${guardianId}/educandos`, { participantId }),
  );
}

/** End a link. The record of it stays in the API. */
export function useUnlinkEducando() {
  return useGuardianChange(({ guardianId, participantId }: LinkVars) =>
    api.delete<GuardianDetail>(`${BASE}/${guardianId}/educandos/${participantId}`),
  );
}

/** Remove the account: turns down a registration, or closes one for good. */
export function useDeleteGuardian() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`${BASE}/${id}`),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: guardianKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ['guardians', 'list'] });
    },
  });
}
